"use client"

import * as React from "react"
import { Clock, Plus, Trash2, Play, Pause, RotateCw } from "lucide-react"
import { toast } from "sonner"

export interface AlarmItem {
  id: string
  time: string
  label: string
  enabled: boolean
}

interface ClockWidgetProps {
  t: any
  lang: string
  userId?: string
  currentTime: string
  clockTab: "alarms" | "timer"
  setClockTab: (tab: "alarms" | "timer") => void
  alarms: AlarmItem[]
  setAlarms: (alarms: AlarmItem[]) => void
  newAlarmTime: string
  setNewAlarmTime: (time: string) => void
  newAlarmLabel: string
  setNewAlarmLabel: (label: string) => void
  timerSeconds: number
  setTimerSeconds: (s: number) => void
  timerActive: boolean
  setTimerActive: (active: boolean) => void
  timerInputMinutes: number
  setTimerInputMinutes: (m: number) => void
  timerDuration?: number
}

export function ClockWidget({
  t,
  lang,
  userId,
  currentTime,
  clockTab,
  setClockTab,
  alarms,
  setAlarms,
  newAlarmTime,
  setNewAlarmTime,
  newAlarmLabel,
  setNewAlarmLabel,
  timerSeconds,
  setTimerSeconds,
  timerActive,
  setTimerActive,
  timerInputMinutes,
  setTimerInputMinutes,
  timerDuration,
}: ClockWidgetProps) {
  const totalDuration =
    timerDuration && timerDuration > 0
      ? timerDuration
      : Math.max(1, Number(timerInputMinutes) || 1) * 60

  const progressPercent =
    totalDuration > 0
      ? Math.min(100, Math.max(0, (timerSeconds / totalDuration) * 100))
      : 0

  const hours = Math.floor(timerSeconds / 3600)
  const minutes = Math.floor((timerSeconds % 3600) / 60)
  const seconds = timerSeconds % 60
  const formattedTime =
    hours > 0
      ? `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      : `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`

  const handleMinutesChange = (newMinutes: number) => {
    const safeMin = Math.max(1, Math.min(180, newMinutes))
    setTimerInputMinutes(safeMin)
    if (!timerActive) {
      setTimerSeconds(safeMin * 60)
    }
  }

  const handleToggleTimer = () => {
    if (!timerActive) {
      if (timerSeconds <= 0) {
        const safeMin = Math.max(1, Number(timerInputMinutes) || 1)
        setTimerSeconds(safeMin * 60)
      }
      setTimerActive(true)
    } else {
      setTimerActive(false)
    }
  }

  const handleResetTimer = () => {
    setTimerActive(false)
    const safeMin = Math.max(1, Number(timerInputMinutes) || 1)
    setTimerSeconds(safeMin * 60)
  }

  return (
    <div className="space-y-4 font-sans">
      <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-800 pb-2">
        <h4 className="text-xs font-bold text-zinc-800 dark:text-zinc-200 flex items-center gap-1.5 font-sans">
          <Clock className="size-3.5 text-[#004EB1]" />
          {t.clockPopoverTitle || "Relógio e Alarmes"}
        </h4>
        <span className="text-[10px] tabular-nums text-zinc-450 dark:text-zinc-500 font-mono">
          {currentTime}
        </span>
      </div>

      <div className="flex gap-1 bg-zinc-100 dark:bg-zinc-850 p-0.5 rounded-lg select-none">
        <button
          type="button"
          onClick={() => setClockTab("alarms")}
          className={`flex-1 text-[10px] font-bold py-1 rounded-md cursor-pointer transition-colors ${clockTab === "alarms"
              ? "bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 shadow-xs"
              : "text-zinc-450 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
        >
          {t.clockTabAlarms || "Alarmes"}
        </button>
        <button
          type="button"
          onClick={() => setClockTab("timer")}
          className={`flex-1 text-[10px] font-bold py-1 rounded-md cursor-pointer transition-colors ${clockTab === "timer"
              ? "bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 shadow-xs"
              : "text-zinc-450 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
            }`}
        >
          {t.clockTabTimers || "Temporizador"}
        </button>
      </div>

      {clockTab === "alarms" && (
        <div className="space-y-3">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (!newAlarmTime) return
              const newAlarm: AlarmItem = {
                id: Math.random().toString(36).substr(2, 9),
                time: newAlarmTime,
                label: newAlarmLabel || (lang === "pt" ? "Despertador" : lang === "es" ? "Despertador" : "Alarm"),
                enabled: true,
              }
              const updatedAlarms = [...alarms, newAlarm]
              setAlarms(updatedAlarms)
              if (userId) localStorage.setItem(`alarms:${userId}`, JSON.stringify(updatedAlarms))
              setNewAlarmLabel("")
              toast.success(t.alarmAdded || "Alarme adicionado!", { duration: 3000 })
            }}
            className="flex gap-2 items-end bg-zinc-50 dark:bg-zinc-950 p-2.5 rounded-xl border border-zinc-150 dark:border-zinc-850"
          >
            <div className="flex-1 space-y-1">
              <label className="text-[9px] font-bold text-zinc-450 dark:text-zinc-550 uppercase tracking-wider block">
                {t.alarmTime || "Hora"}
              </label>
              <input
                type="time"
                value={newAlarmTime}
                onChange={(e) => setNewAlarmTime(e.target.value)}
                required
                className="w-full rounded-md border border-zinc-200 px-2 py-1 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-900 dark:text-white bg-white font-sans"
              />
            </div>
            <div className="flex-1 space-y-1">
              <label className="text-[9px] font-bold text-zinc-450 dark:text-zinc-550 uppercase tracking-wider block">
                {t.alarmLabel || "Nome"}
              </label>
              <input
                type="text"
                placeholder="Acordar"
                value={newAlarmLabel}
                onChange={(e) => setNewAlarmLabel(e.target.value)}
                className="w-full rounded-md border border-zinc-200 px-2 py-1 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-900 dark:text-white bg-white font-sans"
              />
            </div>
            <button
              type="submit"
              className="bg-[#004EB1] hover:bg-[#003d8d] text-white p-1.5 rounded-md cursor-pointer size-8 shrink-0 flex items-center justify-center transition-colors"
            >
              <Plus className="size-4" />
            </button>
          </form>

          <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
            {alarms.length === 0 ? (
              <p className="text-[10px] text-zinc-400 dark:text-zinc-500 italic text-center py-2">
                Nenhum alarme configurado.
              </p>
            ) : (
              alarms.map((alarm) => (
                <div
                  key={alarm.id}
                  className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-zinc-900 border border-zinc-150 dark:border-zinc-800"
                >
                  <div>
                    <span className="text-xs font-bold text-zinc-800 dark:text-zinc-200 font-mono">
                      {alarm.time}
                    </span>
                    <span className="text-[10px] text-zinc-400 ml-2">{alarm.label}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={alarm.enabled}
                      onChange={() => {
                        const updated = alarms.map((a) => (a.id === alarm.id ? { ...a, enabled: !a.enabled } : a))
                        setAlarms(updated)
                        if (userId) localStorage.setItem(`alarms:${userId}`, JSON.stringify(updated))
                      }}
                      className="rounded border-zinc-300 text-[#004EB1] cursor-pointer size-3.5"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const updated = alarms.filter((a) => a.id !== alarm.id)
                        setAlarms(updated)
                        if (userId) localStorage.setItem(`alarms:${userId}`, JSON.stringify(updated))
                      }}
                      className="p-1 rounded text-zinc-400 hover:text-red-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 cursor-pointer"
                    >
                      <Trash2 className="size-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {clockTab === "timer" && (
        <div className="space-y-3.5 text-center">
          {/* Display Principal */}
          <div className="space-y-1">
            <div
              className={`text-3xl font-bold font-mono tracking-wider tabular-nums ${timerActive
                  ? "text-[#004EB1] dark:text-blue-400"
                  : timerSeconds === 0
                    ? "text-red-500 dark:text-red-400 animate-pulse"
                    : "text-zinc-900 dark:text-zinc-100"
                }`}
            >
              {formattedTime}
            </div>
            {timerSeconds === 0 && !timerActive && (
              <span className="text-[11px] font-semibold text-red-500 dark:text-red-400 block">
                {t.timerTriggered || "Tempo esgotado!"}
              </span>
            )}
          </div>

          {/* Barra de Progresso */}
          <div className="w-full bg-zinc-150 dark:bg-zinc-800 rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-full transition-all duration-300 rounded-full ${timerSeconds === 0 ? "bg-red-500" : "bg-[#004EB1]"
                }`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Presets Rápidos */}
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {[1, 5, 10, 15, 25].map((presetMin) => (
              <button
                key={presetMin}
                type="button"
                disabled={timerActive}
                onClick={() => handleMinutesChange(presetMin)}
                className={`text-[10px] font-semibold px-2 py-0.5 rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${timerInputMinutes === presetMin && !timerActive
                    ? "bg-[#004EB1] text-white"
                    : "bg-zinc-100 dark:bg-zinc-800 text-zinc-650 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-750"
                  }`}
              >
                {presetMin}m
              </button>
            ))}
          </div>

          {/* Input de Minutos Personalizado */}
          <div className="flex items-center justify-center gap-2">
            <label className="text-[10px] text-zinc-500 font-medium">
              {t.timerDuration || "Duração"}:
            </label>
            <input
              type="number"
              min={1}
              max={180}
              value={timerInputMinutes || ""}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10)
                if (isNaN(val)) {
                  setTimerInputMinutes(0)
                } else {
                  handleMinutesChange(val)
                }
              }}
              onBlur={() => {
                if (!timerInputMinutes || timerInputMinutes < 1) {
                  handleMinutesChange(5)
                }
              }}
              disabled={timerActive}
              className="w-16 text-center font-mono rounded-md border border-zinc-200 px-2 py-1 text-xs focus-visible:border-[#004EB1] focus-visible:outline-none dark:border-zinc-800 dark:bg-zinc-900 dark:text-white bg-white disabled:opacity-50"
            />
            <span className="text-xs text-zinc-400">min</span>
          </div>

          {/* Controles de Ação */}
          <div className="flex justify-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleToggleTimer}
              className="flex-1 px-3 py-1.5 bg-[#004EB1] hover:bg-[#003d8d] text-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 cursor-pointer shadow-xs"
            >
              {timerActive ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
              {timerActive ? t.pauseTimer || "Pausar" : t.startTimer || "Iniciar"}
            </button>
            <button
              type="button"
              onClick={handleResetTimer}
              className="flex-1 px-3 py-1.5 bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-200 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <RotateCw className="size-3.5" />
              {t.resetTimer || "Reiniciar"}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
